// Every form label in the app goes through these, so every label has its "?" help button (the
// redesign's rule; tests/unit/help.test.ts fails on a raw <label>/<legend> elsewhere):
//   <Field id="close-month" label="Month" help="common.month"> <MonthPicker id="close-month" …/> </Field>
//   <Fieldset label="Cost basis" help="common.costBasis"> …radios… </Fieldset>
//   <Checkbox id="…" label="…" help="…" checked={…} onChange={…} />
//   <LabelText label="Cost basis" help="common.costBasis" /> for a label over a non-input control.
import type { ComponentChildren } from "preact";
import { HelpButton, type HelpId } from "./help";

export interface FieldProps {
  /** The id of the control inside; the label points at it. */
  id: string;
  label: string;
  help: HelpId;
  children: ComponentChildren;
  /** A short hint under the control. */
  hint?: ComponentChildren | undefined;
  /** The field's error, shown under it (give the control aria-describedby={errorId(id)}). */
  error?: string | null | undefined;
  /** The filter bar's smaller label. */
  compact?: boolean | undefined;
  class?: string | undefined;
  /** Visually hide the label (the control's purpose is plain from context); the "?" stays. */
  hideLabel?: boolean | undefined;
}

/** The id of a field's error message, for the control's aria-describedby. */
export function errorId(id: string): string {
  return `${id}-error`;
}

export function hintId(id: string): string {
  return `${id}-hint`;
}

export function Field({ id, label, help, children, hint, error, compact, class: className, hideLabel }: FieldProps) {
  const classes = ["field", compact ? "field-compact" : "", className ?? ""].filter(Boolean).join(" ");
  return (
    <div class={classes}>
      {hideLabel ? (
        // No visible label to sit beside, so the "?" sits beside the control, on its centre line.
        <div class="field-control-row">
          <label for={id} class="field-label visually-hidden">
            {label}
          </label>
          {children}
          <HelpButton topic={help} label={label} />
        </div>
      ) : (
        <>
          <div class="field-label-row">
            <label for={id} class="field-label">
              {label}
            </label>
            <HelpButton topic={help} label={label} />
          </div>
          {children}
        </>
      )}
      {hint && (
        <span class="field-hint" id={hintId(id)}>
          {hint}
        </span>
      )}
      {error ? (
        <p class="field-error" id={errorId(id)} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

export interface FieldsetProps {
  label: string;
  help: HelpId;
  children: ComponentChildren;
  error?: string | null | undefined;
  class?: string | undefined;
}

export function Fieldset({ label, help, children, error, class: className }: FieldsetProps) {
  return (
    <fieldset class={className ? `fieldset ${className}` : "fieldset"}>
      <legend class="fieldset-legend">
        {label}
        <HelpButton topic={help} label={label} />
      </legend>
      {children}
      {error ? (
        <p class="field-error" role="alert">
          {error}
        </p>
      ) : null}
    </fieldset>
  );
}

export interface CheckboxProps {
  id: string;
  label: ComponentChildren;
  /** The label's words for the help button's name when `label` is not plain text. */
  labelText?: string | undefined;
  help: HelpId;
  checked: boolean;
  disabled?: boolean | undefined;
  onChange(checked: boolean): void;
  describedBy?: string | undefined;
}

export function Checkbox({ id, label, labelText, help, checked, disabled, onChange, describedBy }: CheckboxProps) {
  return (
    <div class="field-checkbox">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        aria-describedby={describedBy}
        onChange={(event) => onChange(event.currentTarget.checked)}
      />
      <label for={id}>{label}</label>
      <HelpButton topic={help} label={labelText ?? (typeof label === "string" ? label : "this option")} />
    </div>
  );
}

/** A label-like heading with help, for a control that is not a single input (a segmented control,
 *  the month-range button). Give the control aria-labelledby={labelId}. */
export function LabelText({ id, label, help, compact }: { id: string; label: string; help: HelpId; compact?: boolean }) {
  return (
    <div class="field-label-row">
      <span id={id} class={compact ? "field-label field-label-compact" : "field-label"}>
        {label}
      </span>
      <HelpButton topic={help} label={label} />
    </div>
  );
}

/** A heading (h2/h3) with its help button, for a card whose title names a concept (Rate plan, Who pays whom). */
export function TitleWithHelp({
  level = 2,
  id,
  title,
  help,
}: {
  level?: 2 | 3 | undefined;
  id?: string | undefined;
  title: string;
  help: HelpId;
}) {
  return (
    <div class="card-title-row">
      {level === 2 ? <h2 id={id}>{title}</h2> : <h3 id={id}>{title}</h3>}
      <HelpButton topic={help} label={title} />
    </div>
  );
}

/** An input with a unit beside it ("30 %", "$ 0.00"). */
export function InputAffix({ prefix, suffix, children }: { prefix?: string; suffix?: string; children: ComponentChildren }) {
  return (
    <span class="input-affix">
      {prefix && (
        <span class="input-affix-unit" aria-hidden="true">
          {prefix}
        </span>
      )}
      {children}
      {suffix && (
        <span class="input-affix-unit" aria-hidden="true">
          {suffix}
        </span>
      )}
    </span>
  );
}
